import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Clock, Check, X, AlertCircle, Eye, FileImage, 
  Search, Filter, RefreshCw, ChevronDown
} from "lucide-react";
import Layout from "../../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSubscriptions() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("proof_submitted");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewingProof, setViewingProof] = useState(false);
  const [proofUrl, setProofUrl] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // 'approve' | 'reject' | 'correction'
  const [rejectionReason, setRejectionReason] = useState("");
  const [customDays, setCustomDays] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const url = filterStatus 
        ? `${API}/api/admin/subscriptions?status=${filterStatus}`
        : `${API}/api/admin/subscriptions`;
      const response = await axios.get(url);
      setRequests(response.data.requests);
      setCounts(response.data.counts);
    } catch (error) {
      toast.error("Erro ao carregar solicitações");
    } finally {
      setLoading(false);
    }
  };

  const viewProof = async (request) => {
    setSelectedRequest(request);
    setViewingProof(true);
    setProofLoading(true);
    setProofUrl(null);

    try {
      const response = await axios.get(
        `${API}/api/admin/subscriptions/${request.id}/proof`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      setProofUrl(url);
    } catch (error) {
      toast.error("Erro ao carregar comprovante");
    } finally {
      setProofLoading(false);
    }
  };

  const handleAction = async (action) => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const data = { status: action };
      
      if (action === "rejected" && rejectionReason) {
        data.rejection_reason = rejectionReason;
      }
      
      if (action === "approved" && customDays) {
        data.days = parseInt(customDays);
      }

      const response = await axios.patch(
        `${API}/api/admin/subscriptions/${selectedRequest.id}`,
        data
      );
      
      toast.success(response.data.message);
      setActionDialog(null);
      setSelectedRequest(null);
      setRejectionReason("");
      setCustomDays("");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar ação");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      awaiting_proof: {
        icon: <Clock className="text-yellow-600" size={16} />,
        label: "Aguardando",
        color: "text-yellow-600",
        bg: "bg-yellow-100"
      },
      proof_submitted: {
        icon: <Clock className="text-blue-600" size={16} />,
        label: "Em análise",
        color: "text-blue-600",
        bg: "bg-blue-100"
      },
      approved: {
        icon: <Check className="text-green-600" size={16} />,
        label: "Aprovado",
        color: "text-green-600",
        bg: "bg-green-100"
      },
      rejected: {
        icon: <X className="text-red-600" size={16} />,
        label: "Rejeitado",
        color: "text-red-600",
        bg: "bg-red-100"
      },
      correction_needed: {
        icon: <AlertCircle className="text-orange-600" size={16} />,
        label: "Correção",
        color: "text-orange-600",
        bg: "bg-orange-100"
      }
    };
    return configs[status] || configs.awaiting_proof;
  };

  const getPlanLabel = (planId) => {
    switch(planId) {
      case "base": return "Base";
      case "advanced": return "Avançado";
      case "complete": return "Completo";
      default: return planId;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      req.user_name?.toLowerCase().includes(term) ||
      req.user_email?.toLowerCase().includes(term)
    );
  });

  return (
    <Layout>
      <div data-testid="admin-subscriptions-page" className="overflow-x-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl">Solicitações de Assinatura</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Gerencie pagamentos e aprove assinaturas
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchRequests}
            className="shrink-0"
          >
            <RefreshCw size={16} className="mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { key: "proof_submitted", label: "Em análise", color: "text-blue-600" },
            { key: "awaiting_proof", label: "Aguardando", color: "text-yellow-600" },
            { key: "correction_needed", label: "Correção", color: "text-orange-600" },
            { key: "approved", label: "Aprovados", color: "text-green-600" },
            { key: "rejected", label: "Rejeitados", color: "text-red-600" },
          ].map(({ key, label, color }) => (
            <Card 
              key={key}
              className={`p-3 cursor-pointer transition-all ${
                filterStatus === key ? "ring-2 ring-black" : "hover:bg-secondary/30"
              }`}
              onClick={() => setFilterStatus(filterStatus === key ? "" : key)}
            >
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className={`font-serif text-2xl ${color}`}>{counts[key] || 0}</p>
            </Card>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 loading-skeleton" />
            ))}
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">
              {filterStatus 
                ? `Nenhuma solicitação com status "${getStatusConfig(filterStatus).label}"`
                : "Nenhuma solicitação encontrada"
              }
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => {
              const statusConfig = getStatusConfig(request.status);
              
              return (
                <Card 
                  key={request.id} 
                  className="p-4"
                  data-testid={`subscription-request-${request.id}`}
                >
                  {/* Desktop View */}
                  <div className="hidden lg:flex items-center gap-4">
                    {/* User Info */}
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <div className="w-10 h-10 border flex items-center justify-center bg-secondary shrink-0">
                        <span className="font-serif">
                          {request.user_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{request.user_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{request.user_email}</p>
                      </div>
                    </div>

                    {/* Plan & Period */}
                    <div className="min-w-[120px]">
                      <p className="font-medium">{getPlanLabel(request.plan)}</p>
                      <p className="text-xs text-muted-foreground">
                        {request.period === "monthly" ? "Mensal" : "Semestral"}
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="min-w-[80px]">
                      <p className="font-mono font-medium">${request.amount?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{request.payment_method}</p>
                    </div>

                    {/* Status */}
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-none ${statusConfig.bg}`}>
                      {statusConfig.icon}
                      <span className={`text-xs font-mono uppercase ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>

                    {/* Date */}
                    <div className="min-w-[140px] text-sm text-muted-foreground">
                      {formatDate(request.created_at)}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-auto">
                      {request.proof_file_path && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => viewProof(request)}
                          data-testid={`view-proof-${request.id}`}
                        >
                          <Eye size={14} className="mr-1" />
                          Ver
                        </Button>
                      )}
                      {request.status === "proof_submitted" && (
                        <>
                          <Button 
                            size="sm"
                            onClick={() => { setSelectedRequest(request); setActionDialog("approve"); }}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-${request.id}`}
                          >
                            <Check size={14} className="mr-1" />
                            Aprovar
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedRequest(request); setActionDialog("reject"); }}
                            data-testid={`reject-${request.id}`}
                          >
                            <X size={14} className="mr-1" />
                            Rejeitar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Mobile View */}
                  <div className="lg:hidden">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 border flex items-center justify-center bg-secondary shrink-0">
                          <span className="font-serif">
                            {request.user_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{request.user_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{request.user_email}</p>
                        </div>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-none ${statusConfig.bg}`}>
                        {statusConfig.icon}
                        <span className={`text-xs font-mono uppercase ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Plano</p>
                        <p className="font-medium">{getPlanLabel(request.plan)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Período</p>
                        <p className="font-medium">{request.period === "monthly" ? "Mensal" : "Semestral"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Valor</p>
                        <p className="font-mono font-medium">${request.amount?.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-3 border-t">
                      {request.proof_file_path && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => viewProof(request)}
                          className="flex-1"
                        >
                          <Eye size={14} className="mr-1" />
                          Ver comprovante
                        </Button>
                      )}
                      {request.status === "proof_submitted" && (
                        <>
                          <Button 
                            size="sm"
                            onClick={() => { setSelectedRequest(request); setActionDialog("approve"); }}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <Check size={14} className="mr-1" />
                            Aprovar
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedRequest(request); setActionDialog("reject"); }}
                            className="flex-1"
                          >
                            <X size={14} className="mr-1" />
                            Rejeitar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* View Proof Dialog */}
      <Dialog open={viewingProof} onOpenChange={setViewingProof}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Comprovante de Pagamento</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Usuário</p>
                  <p className="font-medium">{selectedRequest.user_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Valor</p>
                  <p className="font-mono font-medium">${selectedRequest.amount?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Método</p>
                  <p className="font-medium capitalize">{selectedRequest.payment_method}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Data do envio</p>
                  <p className="font-medium">{formatDate(selectedRequest.proof_submitted_at)}</p>
                </div>
              </div>

              <div className="border p-4 min-h-[300px] flex items-center justify-center">
                {proofLoading ? (
                  <div className="loading-skeleton w-12 h-12 rounded-full"></div>
                ) : proofUrl ? (
                  selectedRequest.proof_content_type?.startsWith("image/") ? (
                    <img 
                      src={proofUrl} 
                      alt="Comprovante" 
                      className="max-w-full max-h-[400px] object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <FileImage size={48} className="mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mb-2">
                        {selectedRequest.proof_original_filename}
                      </p>
                      <a 
                        href={proofUrl} 
                        download={selectedRequest.proof_original_filename}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        Baixar arquivo
                      </a>
                    </div>
                  )
                ) : (
                  <p className="text-muted-foreground">Erro ao carregar comprovante</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {selectedRequest?.status === "proof_submitted" && (
              <div className="flex gap-2 w-full">
                <Button 
                  variant="outline"
                  onClick={() => { setViewingProof(false); setActionDialog("correction"); }}
                  className="flex-1"
                >
                  <AlertCircle size={14} className="mr-1" />
                  Solicitar correção
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => { setViewingProof(false); setActionDialog("reject"); }}
                  className="flex-1"
                >
                  <X size={14} className="mr-1" />
                  Rejeitar
                </Button>
                <Button 
                  onClick={() => { setViewingProof(false); setActionDialog("approve"); }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Check size={14} className="mr-1" />
                  Aprovar
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={actionDialog === "approve"} onOpenChange={() => setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar Assinatura</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4">
                <p className="font-medium text-green-800">
                  Você está aprovando a assinatura de {selectedRequest.user_name}.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Plano</p>
                  <p className="font-medium">{getPlanLabel(selectedRequest.plan)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Período</p>
                  <p className="font-medium">
                    {selectedRequest.period === "monthly" ? "30 dias" : "180 dias"}
                  </p>
                </div>
              </div>

              <div>
                <Label>Dias de acesso (opcional)</Label>
                <Input 
                  type="number"
                  placeholder={selectedRequest.period === "monthly" ? "30" : "180"}
                  value={customDays}
                  onChange={(e) => setCustomDays(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Deixe em branco para usar o padrão do período selecionado.
                  Se o usuário tiver dias restantes, serão adicionados automaticamente.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleAction("approved")}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? "Processando..." : "Confirmar aprovação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={actionDialog === "reject"} onOpenChange={() => setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Solicitação</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-red-50 p-4">
                <p className="font-medium text-red-800">
                  Você está rejeitando a solicitação de {selectedRequest.user_name}.
                </p>
              </div>

              <div>
                <Label>Motivo da rejeição *</Label>
                <Textarea 
                  placeholder="Descreva o motivo da rejeição..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleAction("rejected")}
              disabled={processing || !rejectionReason}
              variant="destructive"
            >
              {processing ? "Processando..." : "Confirmar rejeição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction Dialog */}
      <Dialog open={actionDialog === "correction"} onOpenChange={() => setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar Correção</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-orange-50 p-4">
                <p className="font-medium text-orange-800">
                  Você está solicitando que {selectedRequest.user_name} envie um novo comprovante.
                </p>
              </div>

              <div>
                <Label>Motivo da correção</Label>
                <Textarea 
                  placeholder="Ex: Comprovante ilegível, valor incorreto, etc..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleAction("correction_needed")}
              disabled={processing}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {processing ? "Processando..." : "Solicitar correção"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
